<template>
    <div class="fixed inset-0 z-40 overflow-hidden" aria-modal="true" role="dialog">
        <div class="absolute inset-0 bg-gray-600 bg-opacity-60" @click="$emit('close')"></div>
        <section class="absolute inset-y-0 right-0 flex max-w-full pl-10">
            <div class="w-screen max-w-2xl">
                <div class="flex h-full flex-col overflow-y-auto bg-white shadow-xl dark:bg-slate-900 dark:text-white">
                    <div class="flex items-start justify-between border-b border-gray-200 px-6 py-4 dark:border-slate-700">
                        <div>
                            <h2 class="text-lg font-semibold">Detalhes da submissão</h2>
                            <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                Visitor: <span class="font-mono">{{ visitorId }}</span>
                            </p>
                        </div>
                        <button
                            type="button"
                            class="rounded-md text-gray-400 hover:text-gray-700 dark:hover:text-white"
                            @click="$emit('close')"
                        >
                            <font-awesome-icon :icon="['fas', 'xmark']" class="h-5 w-5" />
                        </button>
                    </div>

                    <div class="flex-1 px-6 py-5">
                        <div v-if="loading" class="space-y-3">
                            <div class="h-4 w-1/2 animate-pulse rounded bg-gray-300 dark:bg-slate-700"></div>
                            <div class="h-4 w-1/3 animate-pulse rounded bg-gray-300 dark:bg-slate-700"></div>
                            <div class="h-32 w-full animate-pulse rounded bg-gray-300 dark:bg-slate-700"></div>
                        </div>

                        <div v-else-if="error" class="rounded-md bg-red-100 p-4 text-sm text-red-700 dark:bg-red-900/40 dark:text-red-200">
                            {{ error }}
                        </div>

                        <div v-else class="space-y-6">
                            <section>
                                <h3 class="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">Última submissão</h3>
                                <dl v-if="latest" class="grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
                                    <div>
                                        <dt class="text-xs text-gray-500 dark:text-gray-400">Data</dt>
                                        <dd>{{ formatDateTime(latest.created_at) }}</dd>
                                    </div>
                                    <div>
                                        <dt class="text-xs text-gray-500 dark:text-gray-400">Status</dt>
                                        <dd>
                                            <span
                                                class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                                                :class="latest.passed ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'"
                                            >
                                                {{ latest.passed ? 'Aprovada' : 'Reprovada' }}
                                            </span>
                                        </dd>
                                    </div>
                                    <div>
                                        <dt class="text-xs text-gray-500 dark:text-gray-400">E-mail</dt>
                                        <dd>{{ latest.email || '—' }}</dd>
                                    </div>
                                    <div>
                                        <dt class="text-xs text-gray-500 dark:text-gray-400">CPF</dt>
                                        <dd>{{ latest.cpf || '—' }}</dd>
                                    </div>
                                    <div>
                                        <dt class="text-xs text-gray-500 dark:text-gray-400">IP</dt>
                                        <dd>{{ latest.ip || '—' }}</dd>
                                    </div>
                                    <div>
                                        <dt class="text-xs text-gray-500 dark:text-gray-400">Motivo</dt>
                                        <dd>{{ latest.failed_check || '—' }}</dd>
                                    </div>
                                    <div class="md:col-span-2">
                                        <dt class="text-xs text-gray-500 dark:text-gray-400">User-Agent</dt>
                                        <dd class="break-all text-xs">{{ latest.user_agent || '—' }}</dd>
                                    </div>
                                </dl>
                                <p v-else class="text-sm text-gray-500 dark:text-gray-400">
                                    Sem submissão recente para este visitor.
                                </p>
                            </section>

                            <section v-if="checks.length">
                                <h3 class="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">Checks executados</h3>
                                <ul class="divide-y divide-gray-200 rounded-md border border-gray-200 dark:divide-slate-700 dark:border-slate-700">
                                    <li v-for="(check, index) in checks" :key="index" class="flex items-center justify-between px-3 py-2 text-sm">
                                        <span class="font-medium">{{ check.name || check.key || `Check ${index + 1}` }}</span>
                                        <span
                                            class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                                            :class="check.passed === false ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'"
                                        >
                                            {{ check.passed === false ? 'Reprovou' : 'Passou' }}
                                        </span>
                                    </li>
                                </ul>
                            </section>

                            <section>
                                <h3 class="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">Vendas associadas</h3>
                                <div v-if="sales.length" class="overflow-hidden rounded-md border border-gray-200 dark:border-slate-700">
                                    <table class="min-w-full text-sm">
                                        <thead class="bg-gray-50 dark:bg-slate-800">
                                            <tr>
                                                <th class="px-3 py-2 text-left font-medium">ID</th>
                                                <th class="px-3 py-2 text-left font-medium">Status</th>
                                                <th class="px-3 py-2 text-left font-medium">Valor</th>
                                                <th class="px-3 py-2 text-left font-medium">Data</th>
                                            </tr>
                                        </thead>
                                        <tbody class="divide-y divide-gray-200 dark:divide-slate-700">
                                            <tr v-for="sale in sales" :key="sale.id">
                                                <td class="px-3 py-2 font-mono text-xs">{{ sale.id }}</td>
                                                <td class="px-3 py-2">{{ sale.status || '—' }}</td>
                                                <td class="px-3 py-2">{{ formatCurrency(sale.amount) }}</td>
                                                <td class="px-3 py-2">{{ formatDateTime(sale.created_at) }}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                                <p v-else class="text-sm text-gray-500 dark:text-gray-400">
                                    Sem vendas associadas a este visitor.
                                </p>
                            </section>

                            <section>
                                <h3 class="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">Log ClearSale</h3>
                                <pre v-if="clearSaleLog" class="max-h-64 overflow-auto rounded-md bg-gray-900 p-3 text-xs text-green-200">{{ clearSaleSerialized }}</pre>
                                <p v-else class="text-sm text-gray-500 dark:text-gray-400">
                                    Sem registro ClearSale para este visitor.
                                </p>
                            </section>
                        </div>
                    </div>

                    <div class="flex justify-end border-t border-gray-200 px-6 py-3 dark:border-slate-700">
                        <button
                            type="button"
                            class="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 dark:border-gray-600 dark:bg-slate-700 dark:text-white"
                            @click="$emit('close')"
                        >
                            Fechar
                        </button>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<script>
import axios from "axios";
import { useToast } from "vue-toastification";

export default {
    props: {
        visitorId: { type: String, required: true },
    },
    emits: ["close"],
    setup() {
        return { toast: useToast() };
    },
    data() {
        return {
            loading: false,
            error: null,
            latest: null,
            sales: [],
            clearSaleLog: null,
            checks: [],
        };
    },
    computed: {
        clearSaleSerialized() {
            try {
                return JSON.stringify(this.clearSaleLog, null, 2);
            } catch (e) {
                return "";
            }
        },
    },
    watch: {
        visitorId: {
            immediate: true,
            handler() {
                this.fetch();
            },
        },
    },
    methods: {
        fetch() {
            this.loading = true;
            this.error = null;
            axios
                .get(`/api/operations/checkout/submission/${encodeURIComponent(this.visitorId)}`)
                .then(({ data }) => {
                    const payload = data.data || {};
                    this.latest = payload.latest_submission || null;
                    this.sales = payload.sales || [];
                    this.clearSaleLog = payload.clear_sale_log || null;
                    this.checks = this.latest && Array.isArray(this.latest.checks) ? this.latest.checks : [];
                })
                .catch((err) => {
                    if (err && err.response && err.response.status === 404) {
                        this.error = "Visitor não encontrado.";
                    } else {
                        this.error = "Não foi possível carregar os detalhes.";
                        this.toast.error(this.error);
                    }
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        formatDateTime(value) {
            if (!value) {
                return "—";
            }
            const d = new Date(value);
            if (Number.isNaN(d.getTime())) {
                return value;
            }
            return d.toLocaleString("pt-BR");
        },
        formatCurrency(value) {
            if (value === null || value === undefined) {
                return "—";
            }
            const numeric = typeof value === "number" ? value : Number(value);
            if (Number.isNaN(numeric)) {
                return String(value);
            }
            return new Intl.NumberFormat("pt-BR", {
                style: "currency",
                currency: "BRL",
            }).format(numeric / 100);
        },
    },
};
</script>
